import { useIntl } from "react-intl";
import { SimpleEntityGridColumn } from "src/app/components/grid/SimpleEntityGrid";
import { SetFilterValuesFuncParams } from "ag-grid-enterprise";
import {
    infinityDateColDef,
    infinityNumberColDef,
    infinitySelectColDef,
    infinityTextColDef,
} from "src/app/utils/aggrid/infinityLoadHelper";
import { CellLinks, CellTags, UserCell } from "src/app/components";
import { OnlineOrder } from "src/app/types/orders/onlineOrderPage";

export interface IValuesAsync {
    getManager: (params: SetFilterValuesFuncParams) => void;
    getWriter: (params: SetFilterValuesFuncParams) => void;
    getMoneySite: (params: SetFilterValuesFuncParams) => void;
    getRd: (params: SetFilterValuesFuncParams) => void;
    getWritingType: (params: SetFilterValuesFuncParams) => void;
}

interface IData {
    data: OnlineOrder;
}
export default function useOnlineGridColumns(
    valuesAsync: IValuesAsync
): Array<SimpleEntityGridColumn> {
    const { formatMessage } = useIntl();

    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "id",
            ...infinityNumberColDef("id"),
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "publishedAt",
            ...infinityDateColDef(
                formatMessage({ id: "content.grid.col.publishedAt" }),
                "publishedAt"
            ),
            sort: "desc",
            minWidth: 90,
            maxWidth: 90,
        },
        {
            field: "manager.firstName*manager.lastName",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.manager" }),
                valuesAsync.getManager
            ),
            valueGetter: ({ data }: IData) => (data?.managerName ? data.managerName : ""),
            cellRenderer: ({ data }: any) => <UserCell user={data.managerName} />,
            autoHeight: true,
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "moneySite.domain#platform.name",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.moneySite" }),
                valuesAsync.getMoneySite
            ),
            valueGetter: ({ data }: IData) =>
                data?.platformName ? data?.platformName : data?.moneySiteDomain,
            width: 180,
        },
        {
            field: "rd.domain",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.publishedOn" }),
                valuesAsync.getRd
            ),
            valueGetter: ({ data }: IData) => data?.rdDomain,
            menuTabs: ["filterMenuTab"],
            hide: true,
        },

        {
            field: "rate",
            ...infinityNumberColDef("rate"),
            minWidth: 80,
            maxWidth: 80,
            hide: true,
        },
        {
            field: "contentType*content.writingType",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.contentType" }),
                valuesAsync.getWritingType
            ),
            valueFormatter: ({ data }: IData) =>
                `${formatMessage({
                    id: `content.grid.col.contentType.${data.contentType}`,
                })} ${formatMessage({
                    id: `content.grid.col.writingType.${data.writingType}`,
                })}`,
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "keywords",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.keyword" })),
            valueGetter: ({ data }: IData) => data?.keywords,
            cellRenderer: ({ data }: IData) => <CellTags tags={data?.keywords} />,
            autoHeight: true,
            width: 200,
        },
        {
            field: "links.url#links.anchor",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.links" })),
            valueGetter: ({ data }: IData) =>
                data?.links
                    ? data.links[1]
                        ? `${data.links[0].url} (${data.links[0].anchor})\n ${data.links[1].url} (${data.links[1].anchor})`
                        : `${data.links[0].url} (${data.links[0].anchor})`
                    : "",
            cellRenderer: ({ data }: IData) => <CellLinks links={data.links} />,
            autoHeight: true,
            width: 300,
        },

        {
            field: "url",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.url" })),
        },
        {
            field: "indexed",
            ...infinitySelectColDef(formatMessage({ id: "content.grid.col.indexed" }), undefined, {
                cellRenderer: ( data: any ) => {
                    if (data?.value === "0")
                        return <>{formatMessage({ id: "common.field.notIndexed" })}</>;
                    if (data?.value === "1")
                        return <>{formatMessage({ id: "common.field.indexed" })}</>;
                    if (data?.value === "2")
                        return <>{formatMessage({ id: "common.field.sendToOmegaIndexer" })}</>;
                    if (data?.value === "3")
                        return <>{formatMessage({ id: "common.field.sendToIndexMeNow" })}</>;

                    return <>{formatMessage({ id: `grid.col.filter.${data.value}` })}</>;
                },

                values: [0, 1, 2, 3],
            }),
            cellRenderer: ({ data }: IData) => (
                <>
                    {
                        {
                            0: <>{formatMessage({ id: "common.field.notIndexed" })}</>,
                            1: <>{formatMessage({ id: "common.field.indexed" })}</>,
                            2: <>{formatMessage({ id: "common.field.sendToOmegaIndexer" })}</>,
                            3: <>{formatMessage({ id: "common.field.sendToIndexMeNow" })}</>,
                        }[data.indexed]
                    }
                </>
            ),
            cellClassRules: {
                "order-fail": ({ data }: IData) => data.indexed === 0,
                "order-success": ({ data }: IData) => data.indexed === 1,
                "order-warn": ({ data }: IData) => data.indexed === 2 || data.indexed === 3,
            },
            width: 80,
        },
        {
            field: "indexedAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.indexedAt" }), "indexedAt"),
            minWidth: 90,
            maxWidth: 90,
        },
        {
            field: "indexChecked",
            ...infinityNumberColDef(formatMessage({ id: "content.grid.col.indexChecked" })),
            minWidth: 80,
            maxWidth: 80,
            hide: true,
        },
        {
            field: "writer.firstName*writer.lastName",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.writer" }),
                valuesAsync.getWriter
            ),
            valueGetter: ({ data }: IData) => (data?.writerName ? data.writerName : ""),
            cellRenderer: ({ data }: IData) => <UserCell user={data.writerName} />,
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "deliveredAt",
            ...infinityDateColDef(
                formatMessage({ id: "content.grid.col.deliveredAt" }),
                "deliveredAt"
            ),
            minWidth: 100,
            maxWidth: 100,
            hide: true,
        },
        {
            field: "proofreader.firstName#proofreader.lastName",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.proofreader" })),
            valueGetter: ({ data }: IData) => (data?.proofreaderName ? data.proofreaderName : ""),
            cellRenderer: ({ data }: IData) => <UserCell user={data.proofreaderName} />,
            autoHeight: true,
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "reviewedAt",
            ...infinityDateColDef(
                formatMessage({ id: "content.grid.col.proofreadAt" }),
                "reviewedAt"
            ),
            minWidth: 100,
            maxWidth: 100,
            hide: true,
        },
        {
            field: "wordCount",
            ...infinityNumberColDef(formatMessage({ id: "content.grid.col.wordCount" })),
            minWidth: 65,
            maxWidth: 65,
            hide: true,
        },
        {
            field: "createdAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.createdAt" }), "createdAt"),
            minWidth: 130,
            maxWidth: 130,
            hide: true,
        },
        {
            field: "updatedAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.updatedAt" }), "updatedAt"),
            minWidth: 130,
            maxWidth: 130,
            hide: true,
        },
    ];

    return gridColumns;
}
